
import validator from 'validator';
import * as stringUtil from '../string/stringUtil';
import * as dateUtil from '../dateUtil/dateUtil';
import * as regexUtil from '../regex/regexUtil';
import { getBooleanFlag, FEATURE_FLAGS } from '../featureFlags/openFeature';

export const numericThresholdValue = 0.0009;

//#region Notes

//
// Dated: 2021-11-06
// This validator code was copied today from api which is master reference for validation codes
//
// validate-prefixed methods return error string instead of bool
//

//#endregion

//#region User particulars validation

export function isEmptyOrValidEmailAddress(emailAddress: string): boolean
{
    if (stringUtil.isStringNullOrEmpty(emailAddress))
        return true;

    return validator.isEmail(emailAddress);
}

export function isValidEmailAddress(emailAddress: string): boolean
{
    return !stringUtil.isStringNullOrEmpty(emailAddress);

    if (stringUtil.isStringNullOrEmpty(emailAddress))
        return false;

    return validator.isEmail(emailAddress);
}

export function isValidPhoneNumber(phoneNumber: string): boolean
{
    return !stringUtil.isStringNullOrEmpty(phoneNumber);
    const ukPhoneNumberRegex = /((\+44(\s\(0\)\s|\s0\s|\s)?)|0)7\d{3}(\s)?\d{6}/;

    phoneNumber = stringUtil.trimString(phoneNumber);

    return !!phoneNumber.match(ukPhoneNumberRegex);
}

export function isValidCode(code: string): boolean
{
    return /^[a-zA-Z0-9.]*$/i.test(code); // JBY.amangani.01.pos
}

//#endregion

//#region Number validations

export function isNumeric(value: any): boolean
{
    value = value + "";
    if (stringUtil.isStringNullOrEmpty(value))
        return false;

    const options: validator.IsFloatOptions = {};
    options.locale = undefined;
    return validator.isFloat(value, options);
}

export function isOptionalNumeric(value: any): boolean
{
    if (stringUtil.isStringNullOrEmpty(value))
        return true;

    return isNumeric(value);
}

export function isAllNumeric(...values: any[]): boolean
{
    for (const value of values)
    {
        if (!isNumeric(value))
            return false;
    }

    return true;
}

export function isGreaterThanOrEqualToZero(value: any): boolean
{
    return isNumeric(value) && Number(value) >= 0;
}

export function isGreaterThanZero(value: any): boolean
{
    return isNumeric(value) && Number(value) > numericThresholdValue;
}

export function isOptionalGreaterThanOrEqualToZero(value: any): boolean
{
    if (stringUtil.isStringNullOrEmpty(value))
        return true;

    return isGreaterThanOrEqualToZero(value);
}

export function isOptionalGreaterThanZero(value: any): boolean
{
    if (stringUtil.isStringNullOrEmpty(value))
        return true;

    return isGreaterThanZero(value);
}

export function isBetween(value: any, lower: any, upper: any): boolean
{
    return (
        isNumeric(value) &&
        isNumeric(lower) &&
        isNumeric(upper) &&
        lower <= upper &&
        value >= lower &&
        value <= upper);
}

//#endregion

//#region Integer validations

export function isInteger(value: any): boolean
{
    value = value + "";
    if (stringUtil.isStringNullOrEmpty(value))
        return false;

    return validator.isInt(value);
}

export function isOptionalInteger(value: any): boolean
{
    if (stringUtil.isStringNullOrEmpty(value))
        return true;

    return isInteger(value);
}

//#endregion

//#region Object validations

export function isObject(value: any): boolean
{
    return value != null && typeof value === "object" && !Array.isArray(value);
}

export function isEmptyObject(value: any): boolean
{
    return isObject(value) && Object.keys(value).length === 0;
}

//#endregion

//#region Array validations

export function isArray(value: any): boolean
{
    if (!value) return false;
    return Array.isArray(value);
}

export function isNonEmptyArray(value: any): boolean
{
    if (!value) return false;
    if (!Array.isArray(value)) return false;
    return value.length > 0;
}

//#endregion

//#region Presence validations

export function isPresent(value: any): boolean
{
    return !stringUtil.isStringNullOrEmpty(value);
}

export function isAnyPresent(...values: any[]): boolean
{
    for (const value of values)
    {
        if (!stringUtil.isStringNullOrEmpty(value))
            return true;
    }

    return false;
}

export function isAnyNotPresent(...values: any[]): boolean
{
    for (const value of values)
    {
        if (stringUtil.isStringNullOrEmpty(value))
            return true;
    }

    return false;
}

export function isAllPresent(...values: any[]): boolean
{
    return !isAnyNotPresent(...values);
}

export function isEitherAllPresentOrAllAbsent(...values: any[]): boolean
{
    let allPresent = true;
    let allAbsent = true;

    values.forEach(value => 
    {
        if (stringUtil.isStringNullOrEmpty(value))
        {
            allPresent = false;
        }
        else
        {
            allAbsent = false;
        }
    });

    return allPresent || allAbsent;
}

export function isOnlyOnePresent(...values: any[]): boolean
{
    let result = false;

    for (const value of values)
    {
        if (isPresent(value))
        {
            if (result == true) return false;   // More than one present
            result = true;
        }
    }

    return result;
}

//#endregion

//#region Comparison validations

export function isGreaterThan(value: any, greaterThan: number): boolean
{
    return isNumeric(value) && Number(value) > greaterThan;
}

export function isGreaterThanOrEqualTo(value: any, greaterThan: number): boolean
{
    return isNumeric(value) && Number(value) >= greaterThan;
}

export function isLessThan(value: any, lessThan: number): boolean
{
    return isNumeric(value) && Number(value) < lessThan;
}

export function isLessThanOrEqualTo(value: any, lessThan: number): boolean
{
    return isNumeric(value) && Number(value) <= lessThan;
}

//#endregion

//#region Date/DateTime/Time validations

export function isDate(value: any): boolean
{
    return dateUtil.isValidDate(value);
}

export function isDateTime(value: any): boolean
{
    return dateUtil.isValidDateTime(value);
}

export function isTime(value: any): boolean
{
    return regexUtil.isValid24HourTimeFormat(value);
}

export function isOptionalDateTime(value: any): boolean
{
    if (stringUtil.isStringNullOrEmpty(value))
        return true;

    return dateUtil.isValidDateTime(value);
}

export function isOptionalDate(value: any): boolean
{
    if (stringUtil.isStringNullOrEmpty(value))
        return true;

    return dateUtil.isValidDate(value);
}

export function isOptionalTime(value: any): boolean
{
    if (stringUtil.isStringNullOrEmpty(value))
        return true;

    return regexUtil.isValid24HourTimeFormat(value);
}

export function isDateRange(fromDate: any, toDate: any): boolean
{
    return (
        dateUtil.isValidDate(fromDate) &&
        dateUtil.isValidDate(toDate) &&
        dateUtil.isSameOrBefore(fromDate, toDate));
}

//#endregion

//#region Domain validations

interface OrgModel {
    franchisorId?: string;
    franchiseeId?: string;
    propertyId?: string;
}

export function validateEitherFranchisorOrFranchisee(model: OrgModel): string | null
{
    // Either of franchisorId/franchiseeId node must be exclusive specified

    const franchisorId = stringUtil.trimString(model.franchisorId);
    const franchiseeId = stringUtil.trimString(model.franchiseeId);

    if (!franchisorId && !franchiseeId)
        return "Either of FranchisorId Or FranchiseeId is required";

    if (!(franchisorId ? !franchiseeId : franchiseeId))
        return "Only either one of Franchisor id Or Franchisee id can be specified";

    return null;
}

export function validateWellFormedCreationNode(model: OrgModel): string | null
{
    // Well formed creation node specifies all parent/grandparent nodes

    const franchisorId = stringUtil.trimString(model.franchisorId);
    const franchiseeId = stringUtil.trimString(model.franchiseeId);
    const propertyId = stringUtil.trimString(model.propertyId);

    if (franchisorId == null) return "franchisorId is missing";

    if (propertyId != null)
    {
        if (franchiseeId == null) return "franchiseeId is missing";
    }

    return null;
}

//#endregion

//#region Miscellaneous validations

export function areAllItemsUnique<T>(values: T[], getKey: (item: T) => any): boolean
{
    const keys = new Set(values.map(x => getKey(x)));
    return keys.size == values.length;
}

export function isOptionalColorHexString(value: any): boolean
{
    if (stringUtil.isStringNullOrEmpty(value))
        return true;

    return regexUtil.isValidColorHexString(value);
}

export function isValidEnumeration(value: any, enumeration: Record<string, any>): boolean
{
    for (const key of Object.keys(enumeration)) 
    {
        if (enumeration[key] === value) return true;
    }

    return false;
}

export function isValidSMSSenderName(value: any): boolean
{
    if(stringUtil.isStringNullOrEmpty(value) || typeof value !== 'string')
        return false;
    else if(value.length > 11)
        return false;
    else if(!/^[a-zA-Z0-9]+$/.test(value))
        return false;
    
    return true;
}

//#endregion

//#region Data change validation

export async function hasDataChanged(initialDataStringified: string, currentData: any): Promise<boolean>
{
    // If feature flag is off, always return true (skip data change detection)
    const isEnabled = await getBooleanFlag(FEATURE_FLAGS.VALIDATION_ENABLED);
    if (!isEnabled){
        return true;
    }

    if (!initialDataStringified){
        return false;
    }
    return initialDataStringified !== JSON.stringify(currentData);
}

//#endregion

// Default export for CommonJS compatibility
export default {
    numericThresholdValue,
    isEmptyOrValidEmailAddress,
    isValidEmailAddress,
    isValidPhoneNumber,
    isValidCode,
    isNumeric,
    isOptionalNumeric,
    isAllNumeric,
    isGreaterThanOrEqualToZero,
    isGreaterThanZero,
    isOptionalGreaterThanOrEqualToZero,
    isOptionalGreaterThanZero,
    isBetween,
    isInteger,
    isOptionalInteger,
    isObject,
    isEmptyObject,
    isArray,
    isNonEmptyArray,
    isPresent,
    isAnyPresent,
    isAnyNotPresent,
    isAllPresent,
    isEitherAllPresentOrAllAbsent,
    isOnlyOnePresent,
    isGreaterThan,
    isGreaterThanOrEqualTo,
    isLessThan,
    isLessThanOrEqualTo,
    isDate,
    isDateTime,
    isTime,
    isOptionalDateTime,
    isOptionalDate,
    isOptionalTime,
    isDateRange,
    validateEitherFranchisorOrFranchisee,
    validateWellFormedCreationNode,
    areAllItemsUnique,
    isOptionalColorHexString,
    isValidEnumeration,
    isValidSMSSenderName,
    hasDataChanged
};
